import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectClient } from '@/model/selectors';

import { useShopInsuranceContext } from '@/features/shop-insurance';

interface CoverageGapData {
  coverageGap: number;
  coveredAmount: number;
  totalCost: number;
  coveragePercentage: number;
  hasCoverageGap: boolean;
  hasCoverageCeiling: boolean;
  hasBelowAdlThreshold: boolean;
}

export function useCoverageGap(): CoverageGapData {
  const client = useSelector(selectClient);
  const { maxCoverageQuote } = useShopInsuranceContext();

  // Coverage is measured against the full cost the client faces — professional
  // care PLUS out-of-pocket room & board (non-zero only for Independent Living).
  // A REIMBURSEMENT policy cannot pay room & board, so those clients can never
  // reach 100% and the gap honestly includes it. An INDEMNITY policy can: its
  // surplus cash is applied to room & board and counted in `coveredAmount`.
  const totalCost = client.allPhaseCosts.allPhaseInflatedTotalCost;
  const maxAchievable = maxCoverageQuote?.maxAchievableCoverage ?? totalCost;
  const hasCoverageCeiling = totalCost > 0 && maxAchievable < totalCost;

  const coveredAmount = maxCoverageQuote?.projectedCostCoverage ?? 0;
  const coverageGap = Math.max(0, totalCost - coveredAmount);
  const hasBelowAdlThreshold = (client.intakeSurvey.clientAdlReceivedAssistanceCount ?? 0) < 2;

  const coveragePercentage = useMemo(() => {
    if (totalCost <= 0) return 0;
    return (coveredAmount / totalCost) * 100;
  }, [coveredAmount, totalCost]);

  return {
    coverageGap,
    coveredAmount,
    totalCost,
    coveragePercentage,
    hasCoverageGap: coverageGap > 0,
    hasCoverageCeiling,
    hasBelowAdlThreshold,
  };
}
